
import React, { createContext, useContext, useState, useEffect, useReducer } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { MockAuthService } from './MockAuth';
import { debug } from '../lib/debug';
import { setAuthToken as setDispatchAuthToken, getDispatchApiBaseUrl, setOnUnauthorized } from '../api/dispatchClient';

interface AuthState {
  user: any;
  loading: boolean;
  isAuthenticated: boolean;
  useMockAuth: boolean;
  token: string | null;
  loginTime: string | null;
  renderKey: number;
  softLoggedOut: boolean;
}

interface AuthAction {
  type: string;
  payload?: any;
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  hardLogout: () => void;
  softLogin: (username: string, password: string) => Promise<any>;
  authenticatedFetch: (url: string, options?: any) => Promise<Response>;
  dispatch: React.Dispatch<AuthAction>;
}

interface LoginFormProps {
  onLogin?: (user: any) => void;
  embedded?: boolean;
  onSuccess?: (result: any) => void;
}

// ログインセッションをタブ/ウィンドウ間で共有する BroadcastChannel
const SESSION_CHANNEL = 'auth-session-sync';

interface AuthProviderProps {
  children: React.ReactNode;
}

// Helper to construct URLs
const getApiUrl = (path: string): string => {
  const base = getDispatchApiBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (base.endsWith('/') && cleanPath.startsWith('/')) {
    return `${base.slice(0, -1)}${cleanPath}`;
  }
  if (!base.endsWith('/') && !cleanPath.startsWith('/')) {
    return `${base}/${cleanPath}`;
  }
  return `${base}${cleanPath}`;
};

// 認証状態の型定義
const initialState = {
  user: null,
  loading: true,
  isAuthenticated: false,
  useMockAuth: false,
  token: null,
  loginTime: null,
  renderKey: 0,
  softLoggedOut: false,
};

// Reducer でステート管理を確実にする
function authReducer(state: AuthState, action: AuthAction): AuthState {
  debug.log('AuthContext', 'AuthReducer:', action.type, action.payload);

  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_USER':
      // 同じユーザーの場合は renderKey を更新しない
      const isSameUserSet = state.user && action.payload && state.user.id === action.payload.id;
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
        renderKey: isSameUserSet ? state.renderKey : state.renderKey + 1
      };

    case 'SET_TOKEN':
      return { ...state, token: action.payload };

    case 'SET_MOCK_AUTH':
      return { ...state, useMockAuth: action.payload };

    case 'LOGIN_SUCCESS':
      // 同じユーザーが再定義（セッション更新）された場合は renderKey を更新しない
      // これにより、React のリマウントを防ぎ、作業状態を維持する
      const isSameUser = state.user && action.payload.user && state.user.id === action.payload.user.id;
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loginTime: action.payload.loginTime || new Date().toISOString(),
        isAuthenticated: true,
        loading: false,
        softLoggedOut: false, // ソフトログアウト状態を解除
        renderKey: isSameUser ? state.renderKey : state.renderKey + 1
      };

    case 'LOGOUT':
      return {
        ...initialState,
        loading: false,
        renderKey: state.renderKey + 1
      };

    case 'SOFT_LOGOUT':
      return {
        ...state,
        softLoggedOut: true,
        token: null,
      };

    case 'SOFT_LOGIN_RESTORE':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loginTime: action.payload.loginTime || new Date().toISOString(),
        isAuthenticated: true,
        softLoggedOut: false,
      };

    case 'FORCE_REFRESH':
      return { ...state, renderKey: state.renderKey + 1 };

    default:
      return state;
  }
}

// Context の作成
const AuthContext = createContext<AuthContextValue | null>(null);

// アカウントロック管理関数
const LOCK_DURATION = 30 * 60 * 1000; // 30分（ミリ秒）
const MAX_ATTEMPTS = 5; // 最大試行回数

// アカウントロック状態をチェック
const checkAccountLock = (username: string): { isLocked: boolean; lockedUntil?: number } => {
  const attemptsData = localStorage.getItem('loginAttempts');
  if (!attemptsData) return { isLocked: false };

  try {
    const attempts = JSON.parse(attemptsData);
    if (attempts.username !== username) {
      // 別のユーザーの場合はクリア
      localStorage.removeItem('loginAttempts');
      return { isLocked: false };
    }

    if (attempts.lockedUntil) {
      const now = Date.now();
      if (now < new Date(attempts.lockedUntil).getTime()) {
        // まだロック中
        return { isLocked: true, lockedUntil: new Date(attempts.lockedUntil).getTime() };
      } else {
        // ロック期間が過ぎた
        localStorage.removeItem('loginAttempts');
        return { isLocked: false };
      }
    }

    return { isLocked: false };
  } catch (error) {
    console.error('ロック状態チェックエラー:', error);
    return { isLocked: false };
  }
};

// ログイン失敗を記録
const recordLoginAttempt = (username: string): any => {
  const attemptsData = localStorage.getItem('loginAttempts');
  let attempts: any = { username, count: 0, lastAttempt: new Date().toISOString() };

  if (attemptsData) {
    try {
      const existing = JSON.parse(attemptsData);
      if (existing.username === username) {
        attempts = existing;
      }
    } catch (error) {
      console.error('ログイン試行記録の読み込みエラー:', error);
    }
  }

  attempts.count += 1;
  attempts.lastAttempt = new Date().toISOString();

  if (attempts.count >= MAX_ATTEMPTS) {
    // ロック発動
    attempts.lockedUntil = new Date(Date.now() + LOCK_DURATION).toISOString();
    attempts.isLocked = true;
  }

  localStorage.setItem('loginAttempts', JSON.stringify(attempts));
  return attempts;
};

// ログイン成功時に失敗回数をクリア
const clearLoginAttempts = (username: string): void => {
  const attemptsData = localStorage.getItem('loginAttempts');
  if (attemptsData) {
    try {
      const attempts = JSON.parse(attemptsData);
      if (attempts.username === username) {
        localStorage.removeItem('loginAttempts');
      }
    } catch (error) {
      console.error('ログイン試行記録のクリアエラー:', error);
    }
  }
};

// Provider コンポーネント
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [initialized, setInitialized] = useState(false);

  // セッション同期用 BroadcastChannel（他タブから受信して状態を更新）
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(SESSION_CHANNEL);

    const handler = (e: MessageEvent) => {
      const msg = e.data;
      if (!msg?.type) return;
      debug.log('AuthContext', '他タブからセッション同期:', msg.type);

      if (msg.type === 'session-updated' && msg.token && msg.user && msg.loginTime) {
        // 他タブでログイン/更新 → 自タブも同期
        setDispatchAuthToken(msg.token);
        dispatch({ type: 'LOGIN_SUCCESS', payload: { token: msg.token, user: msg.user, loginTime: msg.loginTime } });
      } else if (msg.type === 'session-soft-logged-out') {
        // 他タブでソフトログアウト → 自タブも同期
        setDispatchAuthToken(null);
        dispatch({ type: 'SOFT_LOGOUT' });
      } else if (msg.type === 'session-hard-logged-out') {
        // 他タブで完全ログアウト → 自タブも同期
        setDispatchAuthToken(null);
        dispatch({ type: 'LOGOUT' });
      }
    };

    channel.addEventListener('message', handler);
    (window as any).__sessionChannel = channel; // login/logout から送信するため

    return () => {
      channel.removeEventListener('message', handler);
      channel.close();
      delete (window as any).__sessionChannel;
    };
  }, []);

  const mockAuth = new MockAuthService();
  const API_BASE = process.env.NODE_ENV === 'development'
    ? 'http://localhost:8000/backend/auth'
    : '/backend/auth';

  // 初期化 - 一度だけ実行
  useEffect(() => {
    if (initialized) return;

    debug.log('AuthContext', '初期化開始');
    setInitialized(true);

    const authMode = localStorage.getItem('authMode');
    if (authMode === 'mock') {
      dispatch({ type: 'SET_MOCK_AUTH', payload: true });
    }

    const initialToken = localStorage.getItem('authToken');
    const initialUserData = localStorage.getItem('userData');

    if (initialToken && initialUserData) {
      try {
        let userData = JSON.parse(initialUserData);
        debug.log('AuthContext', 'ローカルデータから復元:', userData);

        const loginTime = localStorage.getItem('loginTime');

        dispatch({ type: 'SET_TOKEN', payload: initialToken });
        dispatch({ type: 'SET_USER', payload: userData });
        if (loginTime) {
          dispatch({ type: 'LOGIN_SUCCESS', payload: { token: initialToken, user: userData, loginTime } });
        }

        // dispatchClientのトークンも設定
        setDispatchAuthToken(initialToken);

        // company_nameがない場合は、Company API → Staff API の順で取得
        if (!userData.company_name && userData.id) {
          debug.log('AuthContext', 'company_nameがないため、APIから取得します');

          (async () => {
            try {
              // 1. Company API を試す（data.name で会社名取得）
              const companyApiUrl = getApiUrl('/company');
              const companyRes = await fetch(companyApiUrl, {
                headers: { 'Authorization': `Bearer ${initialToken}`, 'Accept': 'application/json' }
              });
              if (companyRes.ok) {
                const companyData = await companyRes.json();
                const name = companyData?.data?.name || companyData?.name;
                if (name) {
                  userData = { ...userData, company_name: name };
                  localStorage.setItem('userData', JSON.stringify(userData));
                  dispatch({ type: 'SET_USER', payload: userData });
                  debug.log('AuthContext', 'Company APIから会社名取得成功:', name);
                  return;
                }
              }

              // 2. フォールバック: スタッフAPIの company relation
              const staffApiUrl = getApiUrl('/staff?per_page=100');
              const staffResponse = await fetch(staffApiUrl, {
                headers: { 'Authorization': `Bearer ${initialToken}`, 'Accept': 'application/json' }
              });
              if (staffResponse.ok) {
                const staffPayload = await staffResponse.json();
                const staffList = Array.isArray(staffPayload) ? staffPayload : (staffPayload?.data || []);
                const currentStaff = staffList.find(s => String(s.id) === String(userData.id));
                if (currentStaff?.company?.company_name) {
                  userData = { ...userData, company_name: currentStaff.company.company_name };
                  localStorage.setItem('userData', JSON.stringify(userData));
                  dispatch({ type: 'SET_USER', payload: userData });
                  debug.log('AuthContext', '初期化時に会社名取得成功:', currentStaff.company.company_name);
                }
              }
            } catch (error) {
              debug.warn('AuthContext', '初期化時の会社名取得失敗:', error);
            }
          })();
        }

        // ローディング完了
        dispatch({ type: 'SET_LOADING', payload: false });
      } catch (error) {
        console.error('AuthProvider: ローカルデータ復元エラー:', error);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } else {
      debug.log('AuthContext', '認証データなし');
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [initialized]);

  // タブ間セッション同期: 別タブでログイン/ログアウトしたら自動反映
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken') {
        if (e.newValue && !e.oldValue) {
          // 別タブでログインされた → セッション復元
          debug.log('AuthContext', '別タブでログイン検出、セッション同期');
          const userData = localStorage.getItem('userData');
          const loginTime = localStorage.getItem('loginTime');
          if (userData) {
            try {
              const user = JSON.parse(userData);
              setDispatchAuthToken(e.newValue);
              dispatch({
                type: 'SOFT_LOGIN_RESTORE',
                payload: { token: e.newValue, user, loginTime: loginTime || new Date().toISOString() },
              });
            } catch (err) {
              debug.warn('AuthContext', 'タブ間同期: userData解析失敗', err);
            }
          }
        } else if (!e.newValue && e.oldValue) {
          // 別タブでログアウトされた → ソフトログアウト
          debug.log('AuthContext', '別タブでログアウト検出、ソフトログアウト');
          setDispatchAuthToken(null);
          dispatch({ type: 'SOFT_LOGOUT' });
        } else if (e.newValue && e.oldValue && e.newValue !== e.oldValue) {
          // 別タブでセッション更新（再ログイン） → トークン更新
          debug.log('AuthContext', '別タブでセッション更新検出');
          const userData = localStorage.getItem('userData');
          const loginTime = localStorage.getItem('loginTime');
          if (userData) {
            try {
              const user = JSON.parse(userData);
              setDispatchAuthToken(e.newValue);
              dispatch({
                type: 'LOGIN_SUCCESS',
                payload: { token: e.newValue, user, loginTime: loginTime || new Date().toISOString() },
              });
            } catch (err) {
              debug.warn('AuthContext', 'タブ間同期: userData解析失敗', err);
            }
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // dispatchClient の 401 コールバックを登録（ソフトログアウトに連携）
  useEffect(() => {
    setOnUnauthorized(() => {
      debug.log('AuthContext', 'dispatchClient 401 detected, triggering soft logout');
      dispatch({ type: 'SOFT_LOGOUT' });
    });
    return () => setOnUnauthorized(null);
  }, []);

  // ログイン関数
  const login = async (username: string, password: string): Promise<any> => {
    debug.log('AuthContext', 'ログイン開始');

    // API Base URL を取得
    const apiUrl = getApiUrl('/auth/login');
    debug.log('AuthContext', 'Login URL:', apiUrl);

    // アカウントロック状態をチェック
    const lockStatus = checkAccountLock(username);
    if (lockStatus.isLocked) {
      const remainingMs = lockStatus.lockedUntil - Date.now();
      const remainingMinutes = Math.ceil(remainingMs / 1000 / 60);
      const remainingSeconds = Math.ceil((remainingMs % 60000) / 1000);
      throw new Error(`このアカウントは一時的にロックされています。\n\nロック解除まで: ${remainingMinutes}分${remainingSeconds}秒\n\nお急ぎの場合は、管理者までご連絡ください。\n連絡先: office@trailernchu.co.jp`);
    }

    try {
      // プロキシ経由でPHP APIにログイン
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          login_id: username,
          password: password,
          device_name: 'Web Browser'
        })
      });

      const data = await response.json();

      // 認証成功（200 OK）
      if (response.ok && data.token && data.staff) {
        const token = data.token;
        let user: any = {
          id: data.staff.id,
          username: data.staff.staff_login_id,
          name: data.staff.staff_name,
          email: data.staff.staff_email,
          level: data.staff.level,
          level_name: data.staff.level_name
        };

        // ログイン成功 - 失敗回数をリセット
        clearLoginAttempts(username);

        // 会社名を取得: Company API → Staff API (フォールバック)
        try {
          // 1. Company API を試す（data.name で会社名取得）
          const companyApiUrl = getApiUrl('/company');
          const companyRes = await fetch(companyApiUrl, {
            headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
          });
          if (companyRes.ok) {
            const companyData = await companyRes.json();
            const companyName = companyData?.data?.name || companyData?.name;
            if (companyName) {
              user = { ...user, company_name: companyName };
              debug.log('AuthContext', 'Company APIから会社名取得成功:', companyName);
            }
          }

          // 2. フォールバック: スタッフAPIの company relation
          if (!user.company_name) {
            const staffApiUrl = getApiUrl('/staff?per_page=100');
            const staffResponse = await fetch(staffApiUrl, {
              headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
            });
            if (staffResponse.ok) {
              const staffPayload = await staffResponse.json();
              const staffList = Array.isArray(staffPayload) ? staffPayload : (staffPayload?.data || []);
              const currentStaff = staffList.find(s => String(s.id) === String(user.id));
              if (currentStaff?.company?.company_name) {
                user = { ...user, company_name: currentStaff.company.company_name };
                debug.log('AuthContext', 'Staff APIから会社名取得成功:', currentStaff.company.company_name);
              }
            }
          }
        } catch (companyError) {
          console.warn('AuthProvider: 会社名取得失敗:', companyError);
        }

        const loginTime = new Date().toISOString();
        localStorage.setItem('authToken', token);
        localStorage.setItem('userData', JSON.stringify(user));
        localStorage.setItem('authMode', 'php');
        localStorage.setItem('loginTime', loginTime);

        // dispatchClientのトークンも設定
        setDispatchAuthToken(token);

        dispatch({ type: 'LOGIN_SUCCESS', payload: { token, user, loginTime } });
        debug.log('AuthContext', 'PHP認証成功');

        // 他タブにセッション更新を通知
        try {
          (window as any).__sessionChannel?.postMessage({
            type: 'session-updated',
            token, user, loginTime,
          });
        } catch { /* ignore */ }

        return { success: true, user };
      } else {
        // ログイン失敗 - 失敗回数を記録
        const attempts = recordLoginAttempt(username);
        const remainingAttempts = MAX_ATTEMPTS - attempts.count;

        if (attempts.isLocked) {
          throw new Error('ログイン試行回数が上限に達しました。\n\nセキュリティのため、このアカウントは30分間ロックされました。\n30分後に再度お試しいただくか、管理者までご連絡ください。\n\n連絡先: office@trailernchu.co.jp');
        } else {
          throw new Error(`ログインに失敗しました。\n\n以下をご確認ください：\n• ユーザーIDとパスワードが正しいか\n• 大文字・小文字が正しいか\n\n残り試行回数: ${remainingAttempts}回\n\n問題が解決しない場合は、管理者までご連絡ください。\n連絡先: office@trailernchu.co.jp`);
        }
      }
    } catch (error) {
      // ネットワークエラーの場合
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('NetworkError')) {
        throw new Error('サーバーに接続できませんでした。\n\nインターネット接続をご確認ください。\n問題が解決しない場合は、管理者までご連絡ください。\n\n連絡先: office@trailernchu.co.jp');
      }
      // JSONパースエラー（Unexpected token '<'...）の場合
      if (error.message.includes('Unexpected token') || error.message.includes('JSON')) {
        console.error('API Error (Parse):', error);
        throw new Error('サーバーエラーが発生しました（APIエンドポイントが無効です）。\n管理者に連絡してください。');
      }
      throw error;
    }
  };

  // ログアウト関数（ソフトログアウト: ページに滞在したままモーダル表示）
  const logout = async (): Promise<void> => {
    debug.log('AuthContext', 'ソフトログアウト開始');

    try {
      if (state.token) {
        const logoutUrl = getApiUrl('/auth/logout');
        await fetch(logoutUrl, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${state.token}` }
        });
      }
    } catch (error) {
      console.warn('AuthProvider: ログアウト API失敗:', error);
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('authMode');
    localStorage.removeItem('loginTime');
    // userData は残す（再ログイン時の表示用）

    setDispatchAuthToken(null);

    dispatch({ type: 'SOFT_LOGOUT' });
    debug.log('AuthContext', 'ソフトログアウト完了');

    // 他タブにも通知
    try {
      (window as any).__sessionChannel?.postMessage({ type: 'session-soft-logged-out' });
    } catch { /* ignore */ }
  };

  // 完全ログアウト（ログイン画面に戻す）
  const hardLogout = (): void => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('authMode');
    localStorage.removeItem('loginTime');
    setDispatchAuthToken(null);
    dispatch({ type: 'LOGOUT' });

    // 他タブにも通知
    try {
      (window as any).__sessionChannel?.postMessage({ type: 'session-hard-logged-out' });
    } catch { /* ignore */ }
  };

  // ソフトログアウト後の再ログイン（login() 成功で LOGIN_SUCCESS → softLoggedOut: false）
  const softLogin = async (username: string, password: string): Promise<any> => {
    return await login(username, password);
  };

  // 認証付きリクエスト
  const authenticatedFetch = async (url: string, options: any = {}): Promise<Response> => {
    if (!state.token) throw new Error('認証が必要です');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${state.token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (response.status === 401) {
      await logout();
      throw new Error('認証が無効です。再度ログインしてください。');
    }

    return response;
  };

  const contextValue = {
    ...state,
    login,
    logout,
    hardLogout,
    softLogin,
    authenticatedFetch,
    dispatch
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// カスタムフック
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth は AuthProvider 内で使用する必要があります');
  }
  return context;
};

// 後方互換性のためのLoginFormコンポーネント
export const LoginForm: React.FC<LoginFormProps> = ({ onLogin, embedded, onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockStatus, setLockStatus] = useState<{ isLocked: boolean; lockedUntil?: number }>({ isLocked: false });
  const [remainingTime, setRemainingTime] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { login } = useAuth();

  // ロック状態をチェック
  useEffect(() => {
    if (!username) return;

    const checkLock = () => {
      const status = checkAccountLock(username);
      setLockStatus(status);

      if (status.isLocked) {
        const remainingMs = status.lockedUntil - Date.now();
        const minutes = Math.floor(remainingMs / 1000 / 60);
        const seconds = Math.floor((remainingMs % 60000) / 1000);
        setRemainingTime(`${minutes}分${seconds}秒`);
      }
    };

    checkLock();
    const interval = setInterval(checkLock, 1000);

    return () => clearInterval(interval);
  }, [username]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!username || !password) return;
    if (lockStatus.isLocked) return;

    setLoading(true);
    setError('');

    try {
      debug.log('AuthContext', 'ログイン試行開始');
      const result = await login(username, password);
      debug.log('AuthContext', 'ログイン成功:', result);

      if (onLogin) {
        onLogin(result.user);
      }
      if (onSuccess) {
        onSuccess(result);
      }

      setUsername('');
      setPassword('');

    } catch (error) {
      console.error('LoginForm: ログインエラー:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '400px', margin: embedded ? '0 auto' : '50px auto', padding: '20px' }}>
      {!embedded && <h2 className="_title_mmhnb_18">ログイン - レンタカー管理システム</h2>}

      {isMobile && (
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 className="_heroTitle_1rh44_46" style={{ margin: '0 0 10px 0', fontSize: '16px', lineHeight: '1.4', fontWeight: 'bold' }}>
            スムーズな旅を、<br /><span style={{ color: '#e6228a' }}>ここから始める。</span>
          </h1>
          <p style={{ margin: 0, fontSize: '11px', color: '#666', lineHeight: '1.5' }}>
            Nレンタカーなら、簡単な手続きですぐに出発。<br />
            チャットでもフォームでも、お好きな方法でご予約いただけます。
          </p>
        </div>
      )}

      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          padding: '10px',
          marginBottom: '15px',
          borderRadius: '4px',
          color: '#c33'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>
            ユーザー名:
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box' as const
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>
            パスワード:
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box' as const
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading || lockStatus.isLocked}
          style={{
            width: '100%',
            padding: '10px',
            background: lockStatus.isLocked ? '#999' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: (loading || lockStatus.isLocked) ? 'not-allowed' : 'pointer',
            opacity: (loading || lockStatus.isLocked) ? 0.6 : 1
          }}
        >
          {lockStatus.isLocked
            ? `ロック中（残り: ${remainingTime}）`
            : loading
              ? 'ログイン中...'
              : 'ログイン'}
        </button>
      </form>
      <p style={{ marginTop: '16px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
        ログイン情報が不明な場合は管理者までお問い合わせください。
      </p>
    </div>
  );
};

export default { AuthProvider, useAuth, LoginForm };